import { useState } from 'react';

type Variant = 'inline' | 'banner' | 'footer';

interface NewsletterFormProps {
  variant?: Variant;
}

export default function NewsletterForm({ variant = 'inline' }: NewsletterFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMsg('Please enter a valid email address.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const body = new URLSearchParams();
      body.append('email', email.trim());

      const res = await fetch('https://readdy.ai/api/form/d7bsfj2qofe8vli40p40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        throw new Error('Submission failed');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  if (variant === 'banner') return <BannerNewsletter email={email} setEmail={setEmail} status={status} errorMsg={errorMsg} onSubmit={handleSubmit} />;
  if (variant === 'footer') return <FooterNewsletter email={email} setEmail={setEmail} status={status} errorMsg={errorMsg} onSubmit={handleSubmit} />;
  return <InlineNewsletter email={email} setEmail={setEmail} status={status} errorMsg={errorMsg} onSubmit={handleSubmit} />;
}

interface FormProps {
  email: string;
  setEmail: (v: string) => void;
  status: 'idle' | 'loading' | 'success' | 'error';
  errorMsg: string;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

/* ── Inline variant (inside post page) ── */
function InlineNewsletter({ email, setEmail, status, errorMsg, onSubmit }: FormProps) {
  return (
    <div className="my-12 rounded-2xl overflow-hidden relative"
      style={{ background: 'linear-gradient(135deg, #fff1f2 0%, #fce7f3 50%, #fdf2f8 100%)' }}>
      <div className="absolute top-0 right-0 w-40 h-40 opacity-10">
        <i className="ri-heart-fill text-rose-400" style={{ fontSize: '10rem', lineHeight: 1 }} />
      </div>
      <div className="relative px-8 py-10">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 flex items-center justify-center text-rose-500">
            <i className="ri-mail-line text-lg" />
          </div>
          <span className="text-xs font-semibold text-rose-500 uppercase tracking-widest">Newsletter</span>
        </div>
        <h3 className="text-2xl font-bold text-stone-800 mb-2" style={{ fontFamily: "'Teko', sans-serif", letterSpacing: '0.02em' }}>
          Get new articles in your inbox
        </h3>
        <p className="text-sm text-stone-500 mb-6 max-w-md">
          3 fresh relationship articles every week — no spam, no fluff. Just honest advice delivered straight to you.
        </p>

        {status === 'success' ? (
          <SuccessMessage />
        ) : (
          <form data-readdy-form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 px-4 py-3 rounded-xl border border-rose-200 bg-white text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-6 py-3 bg-rose-500 text-white text-sm font-semibold rounded-xl hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {status === 'loading' ? 'Subscribing...' : 'Subscribe Free'}
            </button>
          </form>
        )}

        {status === 'error' && (
          <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
            <i className="ri-error-warning-line" /> {errorMsg}
          </p>
        )}
        <p className="mt-3 text-xs text-stone-400">No spam. Unsubscribe anytime.</p>
      </div>
    </div>
  );
}

/* ── Banner variant (homepage section) ── */
function BannerNewsletter({ email, setEmail, status, errorMsg, onSubmit }: FormProps) {
  return (
    <section className="w-full py-16 px-4"
      style={{ background: 'linear-gradient(135deg, #fff1f2 0%, #fce7f3 60%, #fdf2f8 100%)' }}>
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-rose-100 rounded-full text-rose-500">
          <i className="ri-mail-send-line text-2xl" />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-3"
          style={{ fontFamily: "'Teko', sans-serif", letterSpacing: '0.02em' }}>
          Love advice, straight to your inbox
        </h2>
        <p className="text-stone-500 text-sm mb-8 max-w-md mx-auto">
          Join thousands of readers getting 3 new relationship articles every week. Honest, thoughtful, and always free.
        </p>

        {status === 'success' ? (
          <SuccessMessage centered />
        ) : (
          <form data-readdy-form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              className="flex-1 px-4 py-3.5 rounded-xl border border-rose-200 bg-white text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-7 py-3.5 bg-rose-500 text-white text-sm font-semibold rounded-xl hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {status === 'loading' ? 'Subscribing...' : 'Subscribe Free'}
            </button>
          </form>
        )}

        {status === 'error' && (
          <p className="mt-2 text-xs text-red-500 flex items-center gap-1 justify-center">
            <i className="ri-error-warning-line" /> {errorMsg}
          </p>
        )}
        <p className="mt-4 text-xs text-stone-400">No spam. Unsubscribe anytime. We respect your privacy.</p>
      </div>
    </section>
  );
}

/* ── Footer variant (compact, inside footer) ── */
function FooterNewsletter({ email, setEmail, status, errorMsg, onSubmit }: FormProps) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">Stay Updated</h4>
      {status === 'success' ? (
        <div className="flex items-center gap-2 text-sm text-rose-500 font-medium">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-line" />
          </div>
          You&apos;re subscribed!
        </div>
      ) : (
        <form data-readdy-form onSubmit={onSubmit} className="flex flex-col gap-2">
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full px-3 py-2.5 rounded-lg border border-stone-200 bg-white text-sm text-stone-700 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-200 transition-all"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full py-2.5 bg-rose-500 text-white text-sm font-semibold rounded-lg hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </button>
          {status === 'error' && (
            <p className="text-xs text-red-500">{errorMsg}</p>
          )}
          <p className="text-xs text-stone-400">No spam. Unsubscribe anytime.</p>
        </form>
      )}
    </div>
  );
}

/* ── Shared success state ── */
function SuccessMessage({ centered }: { centered?: boolean }) {
  return (
    <div className={`flex flex-col items-${centered ? 'center' : 'start'} gap-2`}>
      <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-full text-rose-500">
        <i className="ri-checkbox-circle-line text-2xl" />
      </div>
      <p className="text-base font-semibold text-stone-800">You&apos;re in!</p>
      <p className="text-sm text-stone-500">Thanks for subscribing. Your first article is on its way.</p>
    </div>
  );
}
